import { supabase } from '../lib/supabase';

/**
 * Transform database workout log to UI format
 */
const transformWorkoutToUI = (dbWorkout) => {
  if (!dbWorkout) return null;
  
  return {
    id: dbWorkout?.id,
    type: dbWorkout?.workout_type,
    name: dbWorkout?.exercise_name,
    duration: dbWorkout?.duration_minutes,
    calories: dbWorkout?.calories_burned,
    steps: dbWorkout?.steps || 0,
    intensity: dbWorkout?.intensity_level,
    notes: dbWorkout?.notes || '',
    date: dbWorkout?.workout_date,
    timestamp: dbWorkout?.created_at
  };
};

/**
 * Service for managing workout logs
 */
export const workoutService = {
  /**
   * Create a new workout log
   */
  async createWorkout(userId, workoutData) {
    const { data, error } = await supabase?.from('workout_logs')?.insert({
        user_id: userId,
        workout_type: workoutData?.type || 'general',
        exercise_name: workoutData?.name,
        completed_sets: workoutData?.completedSets || 0,
        duration_minutes: workoutData?.duration || 0,
        calories_burned: workoutData?.calories || 0,
        steps: workoutData?.steps || 0,
        intensity_level: workoutData?.intensity || 'moderate',
        notes: workoutData?.notes || '',
        workout_date: workoutData?.date || new Date()?.toISOString()?.split('T')?.[0],
        sync_status: 'synced'
      })
      ?.select()
      ?.single();

    if (error) throw error;
    return transformWorkoutToUI(data);
  },

  /**
   * Get workout logs for a user
   */
  async getWorkouts(userId, startDate, endDate) {
    let query = supabase?.from('workout_logs')?.select('*')?.eq('user_id', userId)?.order('workout_date', { ascending: false });

    if (startDate) {
      query = query?.gte('workout_date', startDate);
    }
    if (endDate) {
      query = query?.lte('workout_date', endDate);
    }

    const { data, error } = await query;
    if (error) throw error;
    return (data || [])?.map(transformWorkoutToUI);
  },

  /**
   * Update a workout log
   */
  async updateWorkout(workoutId, updates) {
    const updateData = {};

    if (updates?.exercise_name) updateData.exercise_name = updates?.exercise_name;
    if (updates?.workout_type) updateData.workout_type = updates?.workout_type;
    if (updates?.completed_sets !== undefined) updateData.completed_sets = updates?.completed_sets;
    if (updates?.duration_minutes !== undefined) updateData.duration_minutes = updates?.duration_minutes;
    if (updates?.calories_burned !== undefined) updateData.calories_burned = updates?.calories_burned;
    if (updates?.steps !== undefined) updateData.steps = updates?.steps;
    if (updates?.intensity_level) updateData.intensity_level = updates?.intensity_level;
    if (updates?.notes !== undefined) updateData.notes = updates?.notes;
    if (updates?.workout_date) updateData.workout_date = updates?.workout_date;
    updateData.sync_status = 'synced';

    const { data, error } = await supabase?.from('workout_logs')?.update(updateData)?.eq('id', workoutId)?.select()?.single();

    if (error) throw error;
    return transformWorkoutToUI(data);
  },

  /**
   * Delete a workout log
   */
  async deleteWorkout(workoutId) {
    const { error } = await supabase?.from('workout_logs')?.delete()?.eq('id', workoutId);

    if (error) throw error;
  }
};

/**
 * Service for managing nutrition logs
 */
export const nutritionService = {
  /**
   * Create a new nutrition log
   */
  async createNutritionLog(userId, nutritionData) {
    try {
      // Validate required fields
      if (!userId) {
        throw new Error('User ID is required');
      }
      if (!nutritionData?.mealType) {
        throw new Error('Meal type is required');
      }
      if (!nutritionData?.calories && nutritionData?.calories !== 0) {
        throw new Error('Calories value is required');
      }

      const insertData = {
        user_id: userId,
        meal_type: nutritionData?.mealType,
        food_name: nutritionData?.foodName || nutritionData?.name || 'Alimento',
        calories: nutritionData?.calories || 0,
        protein: nutritionData?.protein || 0,
        carbs: nutritionData?.carbs || 0,
        fats: nutritionData?.fat || 0,
        fiber: nutritionData?.fiber || 0,
        serving_size: `${nutritionData?.portionMultiplier || 1.0}x`,
        logged_date: nutritionData?.date || new Date()?.toISOString()?.split('T')?.[0],
        sync_status: 'synced'
      };

      console.log('Inserting nutrition log:', insertData);

      const { data, error } = await supabase
        ?.from('nutrition_logs')
        ?.insert(insertData)
        ?.select()
        ?.single();

      if (error) {
        console.error('Supabase error:', error);
        throw error;
      }
      
      console.log('Nutrition log created:', data);
      return data;
    } catch (error) {
      console.error('Error in createNutritionLog:', error);
      throw error;
    }
  },

  /**
   * Get nutrition logs for a user
   */
  async getNutritionLogs(userId, startDate, endDate) {
    let query = supabase?.from('nutrition_logs')?.select('*')?.eq('user_id', userId)?.order('logged_date', { ascending: false });

    if (startDate) {
      query = query?.gte('logged_date', startDate);
    }
    if (endDate) {
      query = query?.lte('logged_date', endDate);
    }

    const { data, error } = await query;
    if (error) throw error;
    return data || [];
  },

  /**
   * Delete a nutrition log
   */
  async deleteNutritionLog(logId) {
    const { error } = await supabase?.from('nutrition_logs')?.delete()?.eq('id', logId);

    if (error) throw error;
  }
};

/**
 * Service for managing sleep logs
 */
export const sleepService = {
  /**
   * Create a new sleep log
   */
  async createSleepLog(userId, sleepData) {
    const { data, error } = await supabase?.from('sleep_logs')?.insert({
        user_id: userId,
        sleep_date: sleepData?.date || new Date()?.toISOString()?.split('T')?.[0],
        bedtime: sleepData?.bedtime,
        wake_time: sleepData?.wakeTime,
        duration_hours: sleepData?.durationHours,
        deep_sleep_hours: sleepData?.deepSleepHours || 0,
        light_sleep_hours: sleepData?.lightSleepHours || 0,
        rem_sleep_hours: sleepData?.remSleepHours || 0,
        quality_score: sleepData?.qualityScore || 0,
        consistency_score: sleepData?.consistencyScore || 0,
        notes: sleepData?.notes || ''
      })?.select()?.single();

    if (error) throw error;
    return data;
  },

  /**
   * Get sleep logs for a user
   */
  async getSleepLogs(userId, startDate, endDate) {
    let query = supabase?.from('sleep_logs')?.select('*')?.eq('user_id', userId)?.order('sleep_date', { ascending: false });

    if (startDate) {
      query = query?.gte('sleep_date', startDate);
    }
    if (endDate) {
      query = query?.lte('sleep_date', endDate);
    }

    const { data, error } = await query;
    if (error) throw error;
    return data || [];
  },

  /**
   * Update a sleep log
   */
  async updateSleepLog(logId, updates) {
    const { data, error } = await supabase?.from('sleep_logs')?.update(updates)?.eq('id', logId)?.select()?.single();

    if (error) throw error;
    return data;
  },

  /**
   * Delete a sleep log
   */
  async deleteSleepLog(logId) {
    const { error } = await supabase?.from('sleep_logs')?.delete()?.eq('id', logId);

    if (error) throw error;
  }
};

/**
 * Service for managing activity feed
 */
export const activityFeedService = {
  /**
   * Create a new activity feed entry
   */
  async createActivity(userId, activityData) {
    const { data, error } = await supabase?.from('activity_feed')?.insert({
        user_id: userId,
        activity_type: activityData?.type,
        title: activityData?.title,
        description: activityData?.description || ''
      })?.select()?.single();

    if (error) throw error;
    return data;
  },

  /**
   * Get activity feed for a user
   */
  async getActivities(userId, limit = 10) {
    const { data, error } = await supabase?.from('activity_feed')?.select('*')?.eq('user_id', userId)?.order('created_at', { ascending: false })?.limit(limit);

    if (error) throw error;
    return data || [];
  }
};

/**
 * Service for managing water intake logs
 */
export const waterService = {
  /**
   * Create a new water intake log
   */
  async createWaterLog(userId, waterData) {
    const { data, error } = await supabase?.from('water_intake_logs')?.insert({
        user_id: userId,
        amount_ml: waterData?.amount,
        notes: waterData?.notes || '',
        logged_date: waterData?.date || new Date()?.toISOString()?.split('T')?.[0]
      })?.select()?.single();

    if (error) throw error;
    return data;
  },

  /**
   * Get water intake logs for a user
   */
  async getWaterLogs(userId, startDate, endDate) {
    let query = supabase?.from('water_intake_logs')?.select('*')?.eq('user_id', userId)?.order('logged_date', { ascending: false });

    if (startDate) {
      query = query?.gte('logged_date', startDate);
    }
    if (endDate) {
      query = query?.lte('logged_date', endDate);
    }

    const { data, error } = await query;
    if (error) throw error;
    return data || [];
  },

  /**
   * Update a water intake log
   */
  async updateWaterLog(logId, updates) {
    const { data, error } = await supabase?.from('water_intake_logs')?.update(updates)?.eq('id', logId)?.select()?.single();

    if (error) throw error;
    return data;
  },

  /**
   * Delete a water intake log
   */
  async deleteWaterLog(logId) {
    const { error } = await supabase?.from('water_intake_logs')?.delete()?.eq('id', logId);

    if (error) throw error;
  }
};